/** Учебная диагностика не обращается к рабочему API и не создаёт DEBUG-сеансы. */
import type {
	LlmGateDiagnosticRecord,
	LlmGateDiagnostics,
	LlmGateLogsQuery,
	LlmGateLogsPage,
	LlmGateBodyKind,
	LlmGateBodyList,
	LlmGateBodyContent,
} from '@/api/llmGate';
import { ApiError } from '@/api/client';
const now = Date.now();
const record: LlmGateDiagnosticRecord = {
	id: 1,
	seq: 1,
	occurred_at: now - 1000,
	received_at: now,
	token_id: 42,
	instance_id: 'demo-gateway',
	boot_id: '11111111-1111-4111-8111-111111111111',
	level: 'INFO',
	component: 'request',
	event_code: 'request.completed',
	request_id: '22222222-2222-4222-8222-222222222222',
	trace_id: 'demo-trace',
	route_id: 1,
	route_slug: 'support',
	protocol: 'openai',
	endpoint: 'chat_completions',
	method: 'POST',
	http_status: 200,
	upstream_status: 200,
	gateway_code: 'ok',
	error_kind: '',
	provider_error_code: '',
	duration_ms: 120,
	request_bytes: 200,
	response_bytes: 300,
	segments: 1,
	findings_count: 0,
	masked_count: 0,
	restored_count: 0,
	response_chunks: 0,
	streaming: false,
	partial: false,
	upstream_host: 'provider.example.com',
};
export const demoDiagnosticRecords: LlmGateDiagnosticRecord[] = [
	{
		...record,
		id: 3,
		seq: 3,
		request_id: '33333333-3333-4333-8333-333333333333',
		event_code: 'stream.interrupted',
		component: 'stream',
		partial: true,
		error_kind: 'stream',
		gateway_code: 'stream_interrupted',
		level: 'WARN',
		streaming: true,
		response_chunks: 4,
	},
	record,
	{
		...record,
		id: 2,
		seq: 2,
		request_id: '',
		trace_id: '',
		route_id: 0,
		route_slug: '',
		component: 'config',
		event_code: 'config.applied',
		http_status: 0,
		upstream_status: 0,
	},
];
export const demoDiagnostics: LlmGateDiagnostics = {
	revision: 0,
	enabled_since: null,
	route_id: null,
	token_id: null,
	instance_id: null,
	instances: [
		{
			token_id: 42,
			instance_id: 'demo-gateway',
			alive: true,
			supported: true,
			applied_revision: 0,
			last_delivery_at: now,
			dropped_total: 0,
		},
	],
};
export function demoLogs(query: LlmGateLogsQuery): LlmGateLogsPage {
	const records = demoDiagnosticRecords.filter(
		(r) =>
			(query.from === undefined || r.occurred_at >= query.from) &&
			(query.to === undefined || r.occurred_at <= query.to) &&
			(!query.route_id || String(r.route_id) === String(query.route_id)) &&
			(!query.instance_id ||
				(r.instance_id === query.instance_id && String(r.token_id) === String(query.token_id))) &&
			(!query.level || r.level === query.level) &&
			(!query.trace_id || r.trace_id === query.trace_id),
	);
	return {
		records: query.cursor ? [] : records,
		next_cursor: null,
		snapshot_id: 3,
		from: query.from ?? now - 86400000,
		to: query.to ?? now,
		has_more: false,
		truncated: false,
	};
}

/** Прямой вызов рабочего API в учебном контуре также обслуживается без сети. */
export function demoDiagnosticRead(path: string, params: URLSearchParams): unknown | undefined {
	if (path === '/v1/llm-gate/diagnostics') return demoDiagnostics;
	const bodyPath = /^\/v1\/llm-gate\/logs\/requests\/([^/]+)\/bodies(?:\/([^/]+))?$/.exec(path);
	if (bodyPath)
		return bodyPath[2]
			? demoBody(bodyPath[1], bodyPath[2] as LlmGateBodyKind)
			: demoBodies(bodyPath[1]);
	if (path.startsWith('/v1/llm-gate/logs/requests/'))
		return {
			records: demoDiagnosticRecords.filter(
				(record) => record.request_id === path.split('/').at(-1),
			),
			truncated: false,
		};
	if (path !== '/v1/llm-gate/logs') return undefined;
	return demoLogs({
		limit: 50,
		from: params.has('from') ? Number(params.get('from')) : undefined,
		to: params.has('to') ? Number(params.get('to')) : undefined,
		route_id: params.get('route_id') ?? undefined,
		token_id: params.get('token_id') ?? undefined,
		instance_id: params.get('instance_id') ?? undefined,
		level: params.get('level') ?? undefined,
		trace_id: params.get('trace_id') ?? undefined,
		cursor: params.get('cursor') ?? undefined,
	});
}

/** Все учебные тела созданы из констант и не содержат рабочих данных. */
const demoSavedBodies = {
	client_request: '{"message":"demo@example.test"}',
	upstream_request: '{"message":"[EMAIL_1]"}',
	upstream_response: 'data: {"text":"[EMAIL_1]"}\n\ndata: ',
	client_response: 'data: {"text":"demo@example.test"}\n\ndata: ',
};
function savedDemoText(requestId: string, kind: LlmGateBodyKind): string {
	const text = demoSavedBodies[kind];
	return kind.endsWith('response') && !requestId.startsWith('33333333')
		? `${text}[DONE]\n\n`
		: text;
}
export function demoBodies(requestId: string): LlmGateBodyList {
	if (!demoDiagnosticRecords.some((record) => record.request_id === requestId))
		throw new ApiError(404, 'Request not found');
	const interrupted = requestId.startsWith('33333333');
	return {
		request_id: requestId,
		bodies: (Object.keys(demoSavedBodies) as LlmGateBodyKind[]).map((kind) => {
			const response = kind.endsWith('response');
			const text = savedDemoText(requestId, kind);
			return {
				kind,
				state: 'available',
				reason: interrupted && response ? 'stream_error' : '',
				captured_bytes: new TextEncoder().encode(text).length,
				observed_bytes: new TextEncoder().encode(text).length,
				size_known: !(interrupted && response),
				truncated: false,
				partial: interrupted && response,
				media_type: response ? 'text/event-stream' : 'application/json',
				content_encoding: '',
				captured_at: now - 1000,
				session_revision: 1,
			};
		}),
	};
}
export function demoBody(requestId: string, kind: LlmGateBodyKind): LlmGateBodyContent {
	const metadata = demoBodies(requestId).bodies.find((body) => body.kind === kind);
	if (!metadata) throw new ApiError(404, 'Diagnostic body not found');
	return {
		metadata,
		encoding: 'utf-8',
		content: savedDemoText(requestId, kind),
		sha256:
			!requestId.startsWith('33333333') &&
			(kind === 'upstream_response' || kind === 'client_response')
				? completedDemoBodyHashes[kind]
				: demoBodyHashes[kind],
	};
}
const demoBodyHashes: Record<LlmGateBodyKind, string> = {
	client_request: '325a29820f7aac08692e2d75d546b40f01644c1879bee6f22307f68eb13656cf',
	upstream_request: '20acc003eab34bff0ba618f8aa30f153268a5aff247e023bbe2a674f9b85ecd2',
	upstream_response: '4047e9d19634e3fc4a865b16b7fd98ba0faf40166eb4f42ad5ada5a7f3ecb71f',
	client_response: '211d0083b5061b05c8a48d02e1c2652424bd810fef5f0b6fdc79b480c172e10f',
};
const completedDemoBodyHashes = {
	upstream_response: '54f2d9b5f31a9efc6e668d0a1a646a21bff3dd3cb61d7bd320673fb9de42baa4',
	client_response: '03032a37c633e6814f0f7672d1737f001ba7ddc0b61d777904a398c3d5c1a619',
};
